import classNames from "classnames";
import { observer } from "mobx-react";
import { ReactNode, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { ReactEditor, useFocused, useSelected } from "slate-react";
import { MentionStore } from "../MentionStore";
import { Editor } from "slate";

export const Portal = ({ children }: { children?: ReactNode }) => {
  return typeof document === "object" ? ReactDOM.createPortal(children, document.body) : null;
};

export const Mention = ({ attributes, children, element }) => {
  const selected = useSelected();
  const focused = useFocused();
  const style: React.CSSProperties = {};
  // See if our empty text child has any styling marks applied and apply those
  if (element.children[0].bold) {
    style.fontWeight = "bold";
  }
  if (element.children[0].italic) {
    style.fontStyle = "italic";
  }
  return (
    <span
      {...attributes}
      className={classNames("slate-mention", { selected: selected && focused })}
      contentEditable={false}
      style={style}
      data-id={element.id}
    >
      @{element.label}
      {children}
    </span>
  );
};

export const MentionMenu = observer(({ editor, mentionStore }: { editor: Editor; mentionStore: MentionStore }) => {
  const ref = useRef<HTMLDivElement>();
  const activeItemRef = useRef<HTMLDivElement>();

  useEffect(() => {
    if (mentionStore.target && mentionStore.mentions.length > 0) {
      const el = ref.current;
      const domRange = ReactEditor.toDOMRange(editor, mentionStore.target);
      const rect = domRange.getBoundingClientRect();
      el.style.top = `${rect.top + window.scrollY + 24}px`;
      el.style.left = `${rect.left + window.scrollX}px`;
    }
  }, [mentionStore.mentions.length, editor, mentionStore.index, mentionStore.search, mentionStore.target]);

  // Scroll active item into view when index changes
  useEffect(() => {
    if (activeItemRef.current) {
      activeItemRef.current.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [mentionStore.index]);

  if (!mentionStore.target || !mentionStore.mentions.length) {
    return null;
  }
  return (
    <Portal>
      <div ref={ref} className={classNames(["slate-mention-menu", { ["is-open"]: mentionStore.isMenuVisible }])}>
        {mentionStore.mentions.map((item, i) => (
          <div
            key={item.value}
            ref={i === mentionStore.index ? activeItemRef : null}
            onClick={() => {
              mentionStore.selectMention(item);
            }}
            className={classNames("mention-item", { active: i === mentionStore.index })}
          >
            {item.label}
          </div>
        ))}
      </div>
    </Portal>
  );
});
